import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Alert, Button, Form, ListGroup, Spinner } from 'react-bootstrap';
import { candidacyService } from '@/shared/services/candidacyService';
import { nominationService } from '@/shared/services/nominationService';
import { positionService } from '@/shared/services/positionService';
import CandidatePanel from './CandidatePanel';
import { hasNoteDraft } from './CandidateFeedback';
import PositionNominees from './PositionNominees';
import YourTeam from './YourTeam';
import {
    FILTERS,
    countFor,
    defaultFilter,
    isFilter,
    needsYou,
    secondaryLine,
    staffingText,
    takeLastVisit,
    visibleRows,
} from './candidateList';
import {
    REVIEWABLE,
    STAGE_STEPS,
    StageBadge,
    formatShortDate,
    stepLabelOf,
} from './reviewerStages';

export const NOT_SHARED_TEXT =
    'The program reviews the applicants for this section.';
const GONE_TEXT = 'This position is no longer available.';
const LOAD_ERROR = "The applicants couldn't be loaded.";
const NOMINEES_ERROR = "The nominees couldn't be loaded.";

const HELP_HIDDEN = 'd-none d-lg-block';
const DESKTOP_QUERY = '(min-width: 992px)';
const onDesktop = () =>
    typeof window.matchMedia === 'function' &&
    window.matchMedia(DESKTOP_QUERY).matches;

function SectionSummary({ position, candidacies }) {
    if (!position || position.status === 'pending') return null;
    const review = candidacies.filter((c) =>
        REVIEWABLE.includes(c.stage)
    ).length;
    return (
        <p className="d-lg-none fw-semibold mb-2">
            {`${position.openSlots || 0} open \u00b7 ${review} need review`}
        </p>
    );
}

const headingTag = (level) => `h${Math.min(level, 6)}`;
const PHONE_QUERY = '(max-width: 991.98px)';
const onPhone = () =>
    typeof window.matchMedia === 'function' &&
    window.matchMedia(PHONE_QUERY).matches;

const EMPTY_TEXT = {
    review: 'No one is waiting for review right now.',
    offers: 'No offers are waiting for an answer.',
    all: 'No students to show here.',
};

function Staffing({
    position,
    candidacies,
    nominations,
    since,
    onPick,
    helpOpen = true,
}) {
    const { sentence, expired } = staffingText(
        position,
        candidacies,
        nominations
    );
    const needs = needsYou(candidacies, since);
    const items = [];
    if (needs.review > 0) {
        items.push(
            <Button
                key="review"
                variant="link"
                className="p-0 align-baseline"
                onClick={() => onPick({ filter: 'review' })}
            >
                {`${needs.review} to review`}
                {needs.fresh.size > 0 &&
                    `, ${needs.fresh.size} new since your last visit`}
            </Button>
        );
    }
    if (needs.offers > 0) {
        items.push(
            <Button
                key="offers"
                variant="link"
                className="p-0 align-baseline"
                onClick={() => onPick({ filter: 'offers' })}
            >
                {`${needs.offers} ${needs.offers === 1 ? 'offer' : 'offers'} awaiting an answer`}
                {needs.firstDue &&
                    `, the first due ${formatShortDate(needs.firstDue)}`}
            </Button>
        );
    }
    return (
        <div className="mb-3">
            {sentence && (
                <p className={`mb-1 ${helpOpen ? '' : HELP_HIDDEN}`}>
                    {sentence}
                </p>
            )}
            {expired.map((c) => (
                <p key={c.id} className="mb-1">
                    {'1 expired offer ('}
                    <Button
                        variant="link"
                        className="p-0 align-baseline"
                        onClick={() => onPick({ select: c.id })}
                    >
                        {c.student?.name || 'a student'}
                    </Button>
                    {'): offer again or release the place.'}
                </p>
            ))}
            {items.length > 0 && (
                <p className="mb-0">
                    <span className="fw-semibold">Needs you: </span>
                    {items.map((item, index) => (
                        <React.Fragment key={item.key}>
                            {index > 0 && '; '}
                            {item}
                        </React.Fragment>
                    ))}
                </p>
            )}
        </div>
    );
}

function CandidateRow({
    candidacy,
    selected,
    fresh,
    drafted,
    href,
    onSelect,
    rowRef,
}) {
    const name = candidacy.student?.name || 'A student';
    const line = secondaryLine(candidacy);
    const content = (
        <>
            <span className="flex-grow-1 text-break">
                <span className="fw-semibold">{name}</span>
                {fresh && (
                    <span className="ms-2 small fw-semibold text-primary-emphasis">
                        New
                    </span>
                )}
                {line && (
                    <span className="d-block small text-body-secondary">
                        {line}
                    </span>
                )}
                {drafted && (
                    <span className="d-block small fw-semibold">
                        Note not added yet
                    </span>
                )}
            </span>
            <span className="text-end flex-shrink-0">
                <StageBadge stage={candidacy.stage} />
                {candidacy.positionRank != null && (
                    <span className="d-block small text-body-secondary text-nowrap">
                        {`Rank ${candidacy.positionRank}`}
                    </span>
                )}
            </span>
        </>
    );
    const className = [
        'list-group-item list-group-item-action d-flex align-items-start gap-2 text-start',
        selected ? 'border-start border-4 border-primary bg-body-tertiary' : '',
    ].join(' ');
    const common = {
        className,
        ref: rowRef,
        'aria-current': selected ? 'true' : undefined,
        onClick: (event) => onSelect(candidacy.id, event),
    };
    return (
        <li className="list-unstyled">
            {href ? (
                <a href={href} {...common}>
                    {content}
                </a>
            ) : (
                <button type="button" {...common}>
                    {content}
                </button>
            )}
        </li>
    );
}

/**
 * One position's candidates as a list and a detail: a compact list (search,
 * three filters, finished candidates folded away, the accepted team above
 * it) and the selected student beside it on a wide screen, or as its own
 * screen on a phone. The server decides what the caller may see and do;
 * `mode` decides which fields are rendered.
 *
 * Selection and the list's view (filter and search) are the caller's when
 * it passes `selectedId`/`onSelect` and `view`/`onViewChange` (My LA
 * positions keeps them in the address), else the pipeline's own.
 * `hrefFor(id)` gives each row a real address; `backLabel` names where the
 * phone's Back goes. `knownPosition` is the caller's copy of the position,
 * which gives the places line when the program does not share candidates. `onLoaded({ position,
 * candidacies, nominations })` is called after each load and each change,
 * and also when the program has not shared its candidates (with no
 * candidacies), so a parent can still use the nominees. `onChanged()` is
 * called after a write that can change the position's counts.
 */
export default function PositionPipeline({
    applicationId,
    program,
    mode,
    headingLevel = 3,
    releaseDate = null,
    selectedId: selectedProp,
    onSelect: onSelectProp,
    view: viewProp,
    onViewChange,
    hrefFor,
    knownPosition = null,
    backLabel = 'Back to all students',
    onLoaded,
    onChanged,
}) {
    const baseId = useId();
    const [state, setState] = useState({ status: 'loading' });
    const [notice, setNotice] = useState('');
    const [ownSelected, setOwnSelected] = useState(null);
    const [ownView, setOwnView] = useState({});
    const [showFinished, setShowFinished] = useState(false);
    const [helpOpen, setHelpOpen] = useState(false);
    const [since, setSince] = useState(null);
    const stateRef = useRef(state);
    const onLoadedRef = useRef(onLoaded);
    const onChangedRef = useRef(onChanged);
    const requestRef = useRef(0);
    const rows = useRef({});
    const nomineeRows = useRef({});
    const headingRef = useRef(null);
    const listScroll = useRef(null);
    const lastSelected = useRef(null);
    const pinnedFilter = useRef(null);
    const lastPlace = useRef(null);
    const [focusTarget, setFocusTarget] = useState(null);
    onLoadedRef.current = onLoaded;
    onChangedRef.current = onChanged;

    const controlled = onSelectProp !== undefined;
    const selectedId = controlled ? selectedProp || null : ownSelected;
    const selectedRef = useRef(selectedId);
    const loadedOnce = useRef(false);
    selectedRef.current = selectedId;
    const viewControlled = onViewChange !== undefined;
    const view = viewControlled ? viewProp || {} : ownView;

    useEffect(() => {
        setSince(takeLastVisit(program, applicationId));
    }, [program, applicationId]);

    const commit = useCallback((next) => {
        stateRef.current = next;
        setState(next);
    }, []);

    const load = useCallback(async () => {
        const request = ++requestRef.current;
        commit({ status: 'loading' });
        const [list, noms] = await Promise.allSettled([
            candidacyService.listForPosition(applicationId, program),
            nominationService.getForApplication(applicationId),
        ]);
        if (request !== requestRef.current) return;
        const nominations =
            noms.status === 'fulfilled'
                ? (noms.value?.nominations || []).filter(
                      (n) => n.programType === program
                  )
                : [];
        const nomineesFailed = noms.status === 'rejected';
        if (list.status === 'fulfilled') {
            const { position, candidacies, access } = list.value;
            // A student opened by address: their name takes focus, so the
            // reader starts at the student, not at the top of the page.
            if (
                !loadedOnce.current &&
                selectedRef.current &&
                candidacies.some((c) => c.id === selectedRef.current)
            ) {
                setFocusTarget('heading');
            }
            loadedOnce.current = true;
            commit({
                status: 'ok',
                position,
                candidacies,
                access,
                nominations,
                nomineesFailed,
            });
            onLoadedRef.current?.({ position, candidacies, nominations });
            return;
        }
        const code = list.reason?.response?.status;
        if (code === 403) {
            commit({ status: 'forbidden', nominations, nomineesFailed });
            onLoadedRef.current?.({
                position: null,
                candidacies: [],
                nominations,
            });
        } else if (code === 404) {
            commit({ status: 'gone' });
        } else {
            commit({ status: 'error' });
        }
    }, [applicationId, program, commit]);

    const reload = useCallback(async () => {
        await load();
        onChangedRef.current?.();
    }, [load]);

    useEffect(() => {
        load();
        return () => {
            requestRef.current += 1;
        };
    }, [load]);

    // Focus moves only after the screen it lands on has rendered: the
    // detail's heading when a student opens on a phone or a write moved
    // them, the row when the phone goes back to the list.
    useEffect(() => {
        if (!focusTarget) return;
        const node =
            focusTarget === 'heading'
                ? headingRef.current
                : rows.current[focusTarget] || nomineeRows.current[focusTarget];
        node?.focus({ preventScroll: focusTarget !== 'heading' });
        setFocusTarget(null);
    }, [focusTarget, state, selectedId]);

    // A wide screen opens with the first candidate who needs review, so the
    // detail pane is never empty; a phone starts at the list.
    const autoOpened = useRef(false);
    useEffect(() => {
        if (autoOpened.current || state.status !== 'ok') return;
        autoOpened.current = true;
        if (selectedRef.current || !onDesktop()) return;
        const first = visibleRows(state.candidacies, {
            filter: 'review',
            query: '',
        }).active.find((c) => REVIEWABLE.includes(c.stage));
        if (!first) return;
        if (controlled) onSelectProp(first.id, {});
        else setOwnSelected(first.id);
    }, [state, controlled, onSelectProp]);

    // Back to the list on a phone returns to where the reader was in it.
    useEffect(() => {
        const previous = lastSelected.current;
        lastSelected.current = selectedId;
        if (selectedId || !previous || !onPhone()) return;
        const top = listScroll.current;
        // After the browser's own restoring, which runs on the same Back.
        window.requestAnimationFrame(() => {
            if (top != null) window.scrollTo({ top, behavior: 'instant' });
        });
        setFocusTarget(previous);
    }, [selectedId]);

    const setView = (next) => {
        const merged = { ...view, ...next };
        if (viewControlled) onViewChange(merged);
        else setOwnView(merged);
    };

    const select = (id, { fromList = false } = {}) => {
        if (id === selectedId) return;
        if (fromList && onPhone()) {
            listScroll.current = window.scrollY;
        }
        if (controlled) onSelectProp(id, { fromList });
        else setOwnSelected(id);
        if (id && onPhone()) {
            window.scrollTo({ top: 0, behavior: 'instant' });
            setFocusTarget('heading');
        }
    };

    const rowSelect = (id, event) => {
        event?.preventDefault();
        select(id, { fromList: true });
    };

    const reloadAndFocus = async (key) => {
        await reload();
        if (key?.startsWith('nomination:')) {
            setFocusTarget(key);
        } else if (key) {
            if (controlled) onSelectProp(key, {});
            else setOwnSelected(key);
            setFocusTarget('heading');
        }
    };

    // A write can change the section's counts (an offer holds a place), so
    // the staffing line reads the position again. On a failure the line
    // keeps its last values; the next load corrects them.
    const refreshPosition = async () => {
        try {
            const fresh = await positionService.getPosition(
                applicationId,
                program
            );
            const current = stateRef.current;
            if (fresh?.applicationId && current.status === 'ok') {
                commit({ ...current, position: fresh });
            }
        } catch (_err) {
            // Kept as it was.
        }
    };

    const changed = (updated) => {
        const before = stateRef.current;
        const previous = before.candidacies.find((c) => c.id === updated.id);
        const regrouped = previous && previous.stage !== updated.stage;
        const next = {
            ...before,
            candidacies: before.candidacies.map((c) =>
                c.id === updated.id ? updated : c
            ),
        };
        commit(next);
        // The open candidate's own controls say what the write did.
        setNotice('');
        // The control that made the move may be gone from the new stage.
        if (regrouped) setFocusTarget('heading');
        onLoadedRef.current?.({
            position: next.position,
            candidacies: next.candidacies,
            nominations: next.nominations,
        });
        onChangedRef.current?.();
        refreshPosition();
    };

    const level = headingLevel;
    const Applicants = headingTag(level);

    if (state.status === 'loading') {
        return (
            <div className="py-3" role="status">
                <Spinner animation="border" size="sm" className="me-2" />
                Loading applicants...
            </div>
        );
    }
    if (state.status === 'gone') return <p className="mb-0">{GONE_TEXT}</p>;
    if (state.status === 'error') {
        return (
            <Alert variant="danger">
                <p className="mb-2">{LOAD_ERROR}</p>
                <Button variant="outline-danger" onClick={load}>
                    Retry
                </Button>
            </Alert>
        );
    }

    const nominees = (
        <>
            <PositionNominees
                nominations={state.nominations}
                mode={mode}
                headingLevel={level}
                access={state.access}
                position={state.position}
                releaseDate={releaseDate}
                onChange={(message, changedId) => {
                    // The reload below replaces the list, so the pipeline
                    // keeps what the write said.
                    setNotice(typeof message === 'string' ? message : '');
                    reloadAndFocus(
                        changedId ? `nomination:${changedId}` : null
                    );
                }}
                rowRef={(id, node) => {
                    nomineeRows.current[`nomination:${id}`] = node;
                }}
            />
            {state.nomineesFailed && (
                <p className="mt-2 mb-0">{NOMINEES_ERROR}</p>
            )}
        </>
    );
    if (state.status === 'forbidden') {
        return (
            <div>
                <Staffing
                    position={knownPosition}
                    candidacies={[]}
                    nominations={state.nominations}
                    since={null}
                    onPick={() => {}}
                />
                <YourTeam
                    candidacies={[]}
                    nominations={state.nominations}
                    mode={mode}
                    headingLevel={level}
                />
                <p className="mb-0">{NOT_SHARED_TEXT}</p>
                {nominees}
            </div>
        );
    }

    const { candidacies } = state;
    const query = view.query || '';
    const listFor = (which) => {
        const found = visibleRows(candidacies, { filter: which, query });
        const open =
            showFinished || found.finished.some((c) => c.id === selectedId);
        return {
            ...found,
            finishedOpen: open,
            listed: open ? [...found.active, ...found.finished] : found.active,
        };
    };
    // With no filter chosen, the list opens on what the first student shown
    // needs and stays there, so a decision on the open student does not
    // switch the list being worked through. A student opened from outside
    // the list (an address, the expired-offer link) opens the list they are
    // in.
    if (!pinnedFilter.current) {
        pinnedFilter.current = defaultFilter(candidacies, selectedId);
    }
    let filter = isFilter(view.filter) ? view.filter : pinnedFilter.current;
    let shown = listFor(filter);
    const inList = (list) => list.some((c) => c.id === selectedId);
    const leftList =
        selectedId &&
        !inList(shown.listed) &&
        lastPlace.current?.id === selectedId;
    if (
        selectedId &&
        !leftList &&
        !inList(shown.listed) &&
        !isFilter(view.filter)
    ) {
        pinnedFilter.current = defaultFilter(candidacies, selectedId);
        filter = pinnedFilter.current;
        shown = listFor(filter);
    }
    const { active, finished, finishedOpen, listed } = shown;
    const selected = candidacies.find((c) => c.id === selectedId) || null;
    const index = listed.findIndex((c) => c.id === selectedId);
    let previous = index > 0 ? listed[index - 1] : null;
    let next =
        index >= 0 && index < listed.length - 1 ? listed[index + 1] : null;
    if (index >= 0) {
        lastPlace.current = { id: selectedId, ids: listed.map((c) => c.id) };
    } else if (leftList) {
        // A write moved the open student out of the list: Previous and Next
        // go on from where they stood in it.
        const { ids } = lastPlace.current;
        const at = ids.indexOf(selectedId);
        const still = (id) => listed.find((c) => c.id === id) || null;
        previous = ids.slice(0, at).reverse().map(still).find(Boolean) || null;
        next =
            ids
                .slice(at + 1)
                .map(still)
                .find(Boolean) || null;
    }
    const { fresh } = needsYou(candidacies, since);
    const rankCount = candidacies.filter((c) =>
        REVIEWABLE.includes(c.stage)
    ).length;
    const helpId = `${baseId}-help`;
    const searchId = `${baseId}-search`;
    const listHeadingId = `${baseId}-list`;

    const renderRow = (c) => (
        <CandidateRow
            key={c.id}
            candidacy={c}
            selected={c.id === selectedId}
            fresh={fresh.has(c.id)}
            drafted={c.id !== selectedId && hasNoteDraft(c.id)}
            href={hrefFor ? hrefFor(c.id) : null}
            onSelect={rowSelect}
            rowRef={(node) => {
                rows.current[c.id] = node;
            }}
        />
    );

    let emptyText = null;
    if (candidacies.length === 0) {
        emptyText =
            'No students have applied to this section yet. When a student applies, they appear here.';
    } else if (listed.length === 0 && finished.length === 0) {
        emptyText = query.trim()
            ? `No student matches "${query.trim()}".`
            : EMPTY_TEXT[filter];
    }

    return (
        <div>
            <div role="status">{notice}</div>
            <div className="row g-4">
                <div
                    className={`col-lg-5 ${selected ? 'd-none d-lg-block' : ''}`}
                >
                    <SectionSummary
                        position={state.position}
                        candidacies={candidacies}
                    />
                    <Staffing
                        position={state.position}
                        candidacies={candidacies}
                        nominations={state.nominations}
                        since={since}
                        helpOpen={helpOpen}
                        onPick={({ filter: picked, select: id }) => {
                            if (picked) setView({ filter: picked, query: '' });
                            if (id) select(id, { fromList: true });
                        }}
                    />
                    <YourTeam
                        candidacies={candidacies}
                        nominations={state.nominations}
                        mode={mode}
                        headingLevel={level}
                        selectedId={selectedId}
                        hrefFor={hrefFor}
                        onSelect={rowSelect}
                        emptyClassName={helpOpen ? '' : HELP_HIDDEN}
                    />
                    <section aria-labelledby={listHeadingId}>
                        <Applicants id={listHeadingId} className="h6 mb-2">
                            {`Students who applied (${candidacies.length})`}
                        </Applicants>
                        {candidacies.length > 0 && (
                            <>
                                <Form.Group
                                    controlId={searchId}
                                    className="mb-2"
                                >
                                    <Form.Label className="small mb-1">
                                        Find a student
                                    </Form.Label>
                                    <Form.Control
                                        type="search"
                                        placeholder="Name"
                                        value={query}
                                        onChange={(e) =>
                                            setView({ query: e.target.value })
                                        }
                                    />
                                </Form.Group>
                                <div
                                    className="d-flex flex-wrap gap-2 mb-2"
                                    role="group"
                                    aria-label="Show"
                                >
                                    {FILTERS.map((f) => (
                                        <Button
                                            key={f.key}
                                            variant={
                                                filter === f.key
                                                    ? 'primary'
                                                    : 'outline-primary'
                                            }
                                            aria-pressed={filter === f.key}
                                            onClick={() =>
                                                setView({ filter: f.key })
                                            }
                                        >
                                            {`${f.label} (${countFor(candidacies, f.key)})`}
                                        </Button>
                                    ))}
                                </div>
                                <Button
                                    variant="link"
                                    className="d-lg-none px-0 mb-2"
                                    aria-expanded={helpOpen}
                                    aria-controls={helpId}
                                    onClick={() => setHelpOpen(!helpOpen)}
                                >
                                    {helpOpen
                                        ? 'Hide section details'
                                        : 'Show section details'}
                                </Button>
                                <p
                                    id={helpId}
                                    className={`small text-body-secondary mb-2 ${helpOpen ? '' : HELP_HIDDEN}`}
                                >
                                    {`Hiring steps: ${STAGE_STEPS.map(stepLabelOf).join(', ')}.`}
                                </p>
                            </>
                        )}
                        {emptyText ? (
                            <p className="mb-0">
                                {emptyText}
                                {query.trim() && (
                                    <>
                                        {' '}
                                        <Button
                                            variant="link"
                                            className="p-0 align-baseline"
                                            onClick={() =>
                                                setView({ query: '' })
                                            }
                                        >
                                            Clear the search
                                        </Button>
                                    </>
                                )}
                            </p>
                        ) : (
                            <ListGroup as="ul" className="mb-2 ps-0">
                                {active.map(renderRow)}
                                {finishedOpen && finished.map(renderRow)}
                            </ListGroup>
                        )}
                        {finished.length > 0 &&
                            !finished.some((c) => c.id === selectedId) && (
                                <Button
                                    variant="link"
                                    className="px-0"
                                    aria-expanded={finishedOpen}
                                    onClick={() =>
                                        setShowFinished(!finishedOpen)
                                    }
                                >
                                    {finishedOpen
                                        ? 'Hide finished'
                                        : `Show finished (${finished.length})`}
                                </Button>
                            )}
                    </section>
                    {nominees}
                </div>
                <div
                    className={`col-lg-7 ${selected ? '' : 'd-none d-lg-block'}`}
                >
                    {selected ? (
                        <CandidatePanel
                            key={selected.id}
                            candidacy={selected}
                            position={state.position}
                            access={state.access}
                            mode={mode}
                            siblings={candidacies}
                            rankCount={rankCount}
                            headingLevel={level}
                            headingRef={headingRef}
                            onChange={changed}
                            onReload={reload}
                            onReloadAndFocus={(id, message) => {
                                // The reload replaces the panel that said
                                // what the write did, so the pipeline says it.
                                setNotice(message || '');
                                reloadAndFocus(id);
                            }}
                            onPrevious={
                                previous ? () => select(previous.id) : undefined
                            }
                            onNext={next ? () => select(next.id) : undefined}
                            previousName={previous?.student?.name}
                            nextName={next?.student?.name}
                            positionLabel={
                                index >= 0
                                    ? `Candidate ${index + 1} of ${listed.length}`
                                    : undefined
                            }
                            onBack={() => select(null)}
                            backLabel={backLabel}
                        />
                    ) : (
                        <p className="text-body-secondary border rounded p-4 mb-0">
                            {candidacies.length > 0
                                ? 'Choose a student from the list to read their application, add notes and make an offer.'
                                : 'When students apply, choose one from the list to read their application.'}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}
